// ranking-reveal Remotion composition：排名揭晓（#N → #1 悬念递进）。
// items 数组顺序即揭晓顺序（items[0] 先揭晓），显示按 rank 升序（#1 在顶）。
// 复用 ./tick 的 rankingReveal 决定已揭晓范围；每项揭晓时弹入。
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { rankingReveal } from './tick'
import type { RankingData } from './types'
import type { CompositionProps } from '../../remotion/ScenePlayer'
import { THEME } from '../../remotion/theme'

const PAD_X = 56
const ROW_H = 150

export const RankingRevealComposition: React.FC<CompositionProps> = ({ data }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const d = data as unknown as RankingData
  const time = frame / fps
  const { revealState } = rankingReveal(data, time)

  const items = d.items || []
  // 揭晓顺序：原 index i 在 (i+1)*5s 揭晓；idle（首帧前/全部后）视为全部已揭晓
  const revealed = (i: number) => revealState === 'idle' || time >= (i + 1) * 5 - 0.001
  // 显示顺序：rank 升序（#1 顶），但保留原 index 算揭晓时间
  const ordered = items.map((it, i) => ({ ...it, _i: i })).sort((a, b) => a.rank - b.rank)

  const titleTop = 120
  const listTop = titleTop + (d.title ? 180 : 60)
  const totalH = ordered.length * ROW_H
  // 紧跟标题下方，不垂直居中（避免标题与内容间距过大）
  const top0 = Math.min(listTop, (THEME.H - totalH) / 2 + listTop / 2)

  return (
    <AbsoluteFill style={{ background: (data as any).bgColor || THEME.bg, fontFamily: THEME.fontSans }}>
      {d.title && (
        <div style={{
          position: 'absolute', top: titleTop, left: PAD_X, right: PAD_X,
          color: THEME.w, fontWeight: 800, fontSize: 72, lineHeight: 1.15, textAlign: 'center',
        }}>
          {d.title}
        </div>
      )}
      {ordered.map((it, dispIdx) => {
        const isOn = revealed(it._i)
        // 揭晓瞬间弹入：透明度 + 轻微上移 + 缩放
        const t = time - (it._i + 1) * 5
        const enter = revealState === 'idle' ? 1 : interpolate(t, [-0.001, 0.5], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
        const isChampion = it.rank === 1
        return (
          <div key={it._i} style={{
            position: 'absolute', top: top0 + dispIdx * ROW_H, left: PAD_X, right: PAD_X, height: ROW_H - 24,
            opacity: isOn ? enter : 0,
            transform: `translateY(${(1 - enter) * 24}px)`,
            display: 'flex', alignItems: 'center', gap: 28,
            background: isChampion ? `${it.color}12` : 'transparent',
            borderRadius: 24, padding: isChampion ? '12px 20px' : 0,
            boxShadow: isChampion ? `0 0 48px ${it.color}20` : 'none',
          }}>
            <div style={{
              flex: '0 0 auto',
              width: isChampion ? 120 : 96, height: isChampion ? 120 : 96,
              borderRadius: 28,
              background: isChampion ? `linear-gradient(135deg, ${it.color}, ${it.color}cc)` : THEME.bg3,
              color: isChampion ? '#fff' : THEME.t2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isChampion ? 60 : 48, fontWeight: 800, fontFamily: THEME.fontMono,
              boxShadow: isChampion ? `0 12px 40px ${it.color}55` : 'none',
            }}>
              {it.rank}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                color: isChampion ? THEME.w : THEME.t,
                fontSize: isChampion ? 68 : 52, fontWeight: 800, lineHeight: 1.1,
              }}>
                {it.name}
              </div>
              {it.desc && (
                <div style={{ color: THEME.t, fontSize: 32, marginTop: 8, opacity: 0.8 }}>
                  {it.desc}
                </div>
              )}
            </div>
            <div style={{
              flex: '0 0 auto', width: 12, height: isChampion ? 100 : 80,
              borderRadius: 6, background: it.color, opacity: isChampion ? 1 : 0.4,
            }} />
          </div>
        )
      })}
    </AbsoluteFill>
  )
}
